import React, { useCallback, useEffect, useState } from "react";

import _ from "lodash";
import {
  Button as MUIButton,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";

import {
  changeReturnCondition,
  deleteReturn,
  getReturns,
  updateReturn,
} from "api/shipment";

import { ConfirmDialog, Spacer, StatusLabel } from "components/Custom";
import { ExternalLink } from "components/Custom/Links";
import { Button, Checkbox, Dropdown, InputContainer } from "components/Form";
import { FormattedTextField } from "components/FormElements";
import PageTitle from "components/PageTitle";
import DefaultTable from "components/Table";

import { useLayoutDispatch, popupNotification } from "context/LayoutContext";

import {
  Class,
  Edit,
  FolderSpecial,
  LocalShipping,
  Person,
  QrCode2,
  Photograph,
  Trash,
} from "icons";

import { getCarrierTrackingLink } from "pages/shipments/utils";

import {
  ACUMATICA_BASE_URL,
  CONDITION_LOCATIONS,
  CONDITIONS,
  DETAIL_CONDITIONS,
  RETURN_URL,
} from "utils/constants";
import { initialAPIListValue } from "utils/constants";
import { timezoneFormat } from "utils/date";
import getClearAdornment from "utils/form";

import useStyles from "./styles";
import { Link } from "react-router-dom";
import { exportToExcel } from "utils/exportToExcel";
import { convertDataToTableView } from "utils/convert";
import { useUserState } from "context/UserContext";

const headers = [
  {
    id: "customer",
    render: (r, exported) =>
      exported ? (
        r.customer
      ) : (
        <Grid container alignItems="center" spacing={1}>
          <Grid item xs={1}>
            {r.is_published ? (
              <div
                style={{
                  backgroundColor: "#00CC00",
                  width: "5px",
                  height: "30px",
                  position: "relative",
                  left: "-5px",
                  borderRadius: "3px",
                }}
              />
            ) : null}
          </Grid>
          <Grid item xs={9}>
            <span title="Customer">{r.customer}</span>
          </Grid>
          {r.details?.filter((d) => d.attachments?.length > 0).length > 0 && (
            <Grid item xs={1}>
              <span title="Attachments">
                <Photograph color="#bbbb0050" />
              </span>
            </Grid>
          )}
        </Grid>
      ),
  },
  {
    id: "product_sku",
    render: (r, exported) => {
      if (exported) {
        return r.details
          .map((d) =>
            d.product ? `${d.product.sku} - (${d.condition})` : "Not Ours",
          )
          .toString();
      }
      const itemCount = r.details.length;
      return !itemCount
        ? "-"
        : itemCount === 1
        ? r.details[0].product?.sku
        : `Multiple Items (${itemCount})`;
    },
  },
  { id: "po_number" },
  {
    id: "tracking_number",
    render: (r, exported) => {
      if (exported) {
        return r.tracking_number;
      }
      const carrier = r.tracking_number.startsWith("1Z") ? "UPS" : "FEDEX";
      return (
        <ExternalLink
          to={getCarrierTrackingLink(carrier, r.tracking_number)}
          children={r.tracking_number}
        />
      );
    },
  },
  {
    id: "return_number",
    label: "RC Number",
    render: (r, exported) => {
      if (exported) {
        return r.return_number;
      }
      return r.return_number ? (
        <ExternalLink
          to={`${ACUMATICA_BASE_URL}${RETURN_URL}${r.return_number}`}
          children={r.return_number}
        />
      ) : (
        "-"
      );
    },
  },
  { id: "note", render: (r) => r.note || "-" },
  {
    id: "condition",
    render: (r, exported) =>
      exported ? (
        r.condition
      ) : (
        <StatusLabel status={r.condition || "Not Ours"} />
      ),
  },
  { id: "created_at", render: (r) => timezoneFormat(r.created_at) },
];

const exportHeaders = [
  ...headers,
  {
    id: "is_published",
    label: "Is Published",
    render: (r) => (r.is_published ? "Published" : "Not Published"),
  },
];

export default function Returns() {
  const layoutDispatch = useLayoutDispatch();
  const { permissions } = useUserState();
  const [returns, setReturns] = useState(initialAPIListValue);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [params, setParams] = useState({
    product_sku: "",
    customer: "",
    po_number: "",
    return_number: "",
    is_published: "",
    tracking_number: "",
    not_ours: "",
    seller_id: 2,
    with_details: true,
  });
  const [orderDirection, setOrderDirection] = useState("desc");
  const [orderBy, setOrderBy] = useState("created_at");
  const [exportDisabled, setExportDisabled] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [selectedReturn, setSelectedReturn] = useState({});
  const [detailModal, setDetailModal] = useState(false);

  useEffect(() => {
    if (returns.isFetching) {
      let filters = {
        ...params,
        limit: rowsPerPage,
        offset: rowsPerPage * page,
        paginated: true,
      };

      if (orderDirection && orderBy) {
        filters.ordering = `${orderDirection === "asc" ? "" : "-"}${orderBy}`;
      }

      getReturns({
        params: filters,
        setFunc: (res) => {
          setReturns({
            ...res,
            items: _.uniqBy(res.items, (i) => i.id),
          });
        },
        paginated: true,
      });
    }
  }, [params, page, rowsPerPage, returns.isFetching, orderDirection, orderBy]);

  const handleCheckBox = (prop) => () => {
    setParams((prev) => ({
      ...prev,
      [prop]: params[prop] ? "" : !params[prop],
    }));
    setPage(0);
    setReturns((prev) => ({ ...prev, isFetching: true }));
  };

  const onSort = (header) => {
    const isAsc = orderBy === header && orderDirection === "asc";
    setOrderDirection(isAsc ? "desc" : "asc");
    setOrderBy(header);
    setReturns((prev) => ({ ...prev, isFetching: true }));
  };

  const handleChangePage = (e, newPage) => {
    setPage(newPage);
    setReturns((prev) => ({ ...prev, isFetching: true }));
  };

  const handleChangeRowsPerPage = ({ target: { value } }) => {
    setRowsPerPage(parseInt(value, 10));
    debouncedSearchHandler();
  };

  const handleChangeFilterInput = (prop) => (event) => {
    setParams({ ...params, [prop]: event.target.value });
    debouncedSearchHandler();
  };

  const clearHandler = (input) => {
    setParams({ ...params, [input]: "" });
    debouncedSearchHandler();
  };

  const clearAdornment = (input) =>
    getClearAdornment(input, clearHandler, params);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearchHandler = useCallback(
    _.debounce(() => {
      setReturns({ ...returns, isFetching: true });
    }, 500),
    [],
  );

  const hasDeletePermission = permissions?.includes("shipment.delete_return");

  // The delete column is appended rather than baked into `headers` so the Excel export keeps
  // reporting data only.
  const tableHeaders = hasDeletePermission
    ? [
        ...headers,
        {
          id: "actions",
          label: "Actions",
          render: (r) => (
            <IconButton
              size="small"
              aria-label="delete-return"
              onClick={(event) => {
                event.stopPropagation();
                setDeleteTarget(r);
              }}
            >
              <Trash color="red" size={0.8} />
            </IconButton>
          ),
        },
      ]
    : headers;

  const handleDeleteReturn = (row) => {
    popupNotification({
      dispatch: layoutDispatch,
      message: "Deleting return. Please wait..",
      status: "info",
    });
    deleteReturn({
      id: row.id,
      responseSetter: (response) => {
        const { success, message } = response || {};
        popupNotification({
          dispatch: layoutDispatch,
          message: message || "Server Error",
          status: success ? "success" : "error",
        });
        if (success) {
          setReturns((prev) => ({ ...prev, isFetching: true }));
        }
      },
    });
  };

  const handleCloseModal = (status = false) => {
    if (status) {
      setReturns((prev) => ({ ...prev, isFetching: true }));
    }
    setDetailModal(false);
    setSelectedReturn({});
  };

  const exportData = (data) => {
    setExportDisabled(true);
    const today = new Date();
    exportToExcel(
      [],
      `Returns-${today.toLocaleDateString()}`,
      true,
      convertDataToTableView(data, exportHeaders),
    );
    setExportDisabled(false);
  };

  const handleExportData = () => {
    if (returns.items.length === returns.count) {
      exportData(returns.items);
    } else {
      getReturns({
        params: {
          ...params,
          limit: returns.count,
        },
        setFunc: ({ items }) => exportData(items),
        paginated: true,
      });
    }
  };

  return (
    <>
      <PageTitle
        title="Returns"
        button={
          <MUIButton
            component={Link}
            to="/app/create-return"
            variant="contained"
            size="medium"
            color="secondary"
          >
            Create Return
          </MUIButton>
        }
      />
      <InputContainer>
        <Grid container>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <FormattedTextField
              fullWidth
              id="sku"
              label="SKU"
              variant="outlined"
              value={params.product_sku}
              onChange={handleChangeFilterInput("product_sku")}
              InputProps={clearAdornment("product_sku")}
              LeftIcon={QrCode2}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <FormattedTextField
              fullWidth
              id="po_number"
              label="PO Number"
              variant="outlined"
              value={params.po_number}
              onChange={handleChangeFilterInput("po_number")}
              InputProps={clearAdornment("po_number")}
              LeftIcon={FolderSpecial}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <FormattedTextField
              fullWidth
              id="return_number"
              label="RC Number"
              variant="outlined"
              value={params.rc_number}
              onChange={handleChangeFilterInput("return_number")}
              InputProps={clearAdornment("return_number")}
              LeftIcon={FolderSpecial}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <FormattedTextField
              fullWidth
              id="tracking_number"
              label="Tracking Number"
              variant="outlined"
              value={params.tracking_number}
              onChange={handleChangeFilterInput("tracking_number")}
              InputProps={clearAdornment("tracking_number")}
              LeftIcon={LocalShipping}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <FormattedTextField
              fullWidth
              id="customer"
              label="Customer"
              variant="outlined"
              value={params.customer}
              onChange={handleChangeFilterInput("customer")}
              InputProps={clearAdornment("customer")}
              LeftIcon={Person}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Dropdown
              id="is_published"
              title="Published"
              value={params.is_published_acumatica}
              onChange={handleChangeFilterInput("is_published")}
              items={[
                {
                  value: false,
                  label: "Not Published",
                },
                {
                  value: true,
                  label: "Published",
                },
              ]}
              LeftIcon={Class}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Dropdown
              id="condition"
              title="Condition"
              value={params.condition}
              onChange={handleChangeFilterInput("condition")}
              items={CONDITIONS.map((c) => ({ value: c, label: c }))}
              LeftIcon={Class}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Checkbox
              id="not_ours"
              label="Not Ours"
              checked={params.not_ours}
              onChange={handleCheckBox("not_ours")}
              color="blue"
            />
          </Grid>
        </Grid>
      </InputContainer>
      <Grid item lg={12} md={12} sm={12} xs={12}>
        <DefaultTable
          headers={tableHeaders}
          data={returns}
          filters={params}
          page={page}
          rowsPerPage={rowsPerPage}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          sortActions={{ onSort, orderBy, orderDirection }}
          rowOnClick={(r) => {
            setSelectedReturn(r);
            setDetailModal(true);
          }}
          handleExportData={handleExportData}
          exportDisabled={exportDisabled}
          setExportDisabled={setExportDisabled}
        />
        {detailModal ? (
          <ReturnDetails
            open={detailModal}
            onClose={handleCloseModal}
            returnData={selectedReturn}
          />
        ) : null}
        <ConfirmDialog
          open={!!deleteTarget}
          title="Delete Return"
          confirmText="Delete"
          headerColor="red"
          children={
            <>
              <Typography>
                {`This action will delete the return ${
                  deleteTarget?.po_number || ""
                } with all of its items and photos. Please confirm the action?`}
              </Typography>
              {deleteTarget?.is_published ? (
                <Alert severity="warning">
                  This return was already published, so its items are on the
                  shelves. Deleting the record does not take them back off.
                </Alert>
              ) : null}
            </>
          }
          onConfirm={() => handleDeleteReturn(deleteTarget)}
          onClose={() => setDeleteTarget(null)}
        />
      </Grid>
    </>
  );
}

function ReturnDetails({ returnData, open, onClose }) {
  const classes = useStyles();
  const layoutDispatch = useLayoutDispatch();
  const { permissions } = useUserState();
  const [isFetching, setIsFetching] = useState(false);
  // Item edits keep the dialog open, so the list is held locally and the page is
  // refreshed once the dialog closes.
  const [details, setDetails] = useState(returnData.details || []);
  const [isChanged, setIsChanged] = useState(false);
  const [formData, setFormData] = useState({
    customer: returnData.customer,
    po_number: returnData.po_number,
    tracking_number: returnData.tracking_number,
    note: returnData.note,
  });

  const hasUpdatePermission = permissions?.includes("shipment.change_return");
  const canEditInfo = !returnData.is_published && hasUpdatePermission;

  const handleChangeFilter = (prop) => ({ target: { value } }) => {
    setFormData((prev) => ({ ...prev, [prop]: value }));
  };

  const handleUpdate = () => {
    setIsFetching(true);
    updateReturn({
      id: returnData.id,
      body: { ...formData },
      responseSetter: (res) => {
        setIsFetching(false);
        const { id } = res;
        if (!id) {
          return popupNotification({
            dispatch: layoutDispatch,
            message: "Server Error",
          });
        }
        onClose(true);
        popupNotification({
          dispatch: layoutDispatch,
          message: "Done!",
          status: "success",
        });
      },
    });
  };

  const handleConditionChanged = (detailId, condition) => {
    setDetails((prev) =>
      prev.map((d) =>
        // A line with a product is shelved by the change if it was not already.
        d.id === detailId
          ? { ...d, condition, is_published: d.is_published || !!d.product }
          : d,
      ),
    );
    setIsChanged(true);
  };

  const infoFields = [
    { id: "po_number", label: "PO Number", icon: FolderSpecial },
    { id: "customer", label: "Customer", icon: Person },
    { id: "tracking_number", label: "Tracking Number", icon: LocalShipping },
    { id: "note", label: "Note", icon: Edit },
  ];

  return (
    <Dialog
      open={open}
      onClose={() => onClose(isChanged)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle disableTypography>
        <Typography variant="h6">{`PO ${returnData.po_number || "-"}`}</Typography>
        <Typography variant="body2" className={classes.dialogSubtitle}>
          {`${returnData.customer || "-"} · ${returnData.tracking_number}`}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="subtitle2" className={classes.sectionTitle}>
          Return Info
        </Typography>
        <Grid container spacing={2}>
          {infoFields.map(({ id, label, icon }) => (
            <Grid item xs={12} sm={6} key={id}>
              <FormattedTextField
                fullWidth
                id={id}
                label={label}
                variant="outlined"
                value={formData[id] || ""}
                onChange={handleChangeFilter(id)}
                disabled={!canEditInfo || isFetching}
                LeftIcon={icon}
              />
            </Grid>
          ))}
          {canEditInfo ? (
            <Grid item xs={12}>
              <Button
                color="primary"
                onClick={handleUpdate}
                disabled={isFetching}
              >
                Update
              </Button>
            </Grid>
          ) : null}
          {returnData.return_number ? (
            <Grid item xs={12}>
              <ExternalLink
                to={`${ACUMATICA_BASE_URL}${RETURN_URL}${returnData.return_number}`}
              >
                RC Number : {returnData.return_number}
              </ExternalLink>
            </Grid>
          ) : null}
        </Grid>
        <Spacer height="1.5rem" />
        <Typography variant="subtitle2" className={classes.sectionTitle}>
          {`Items (${details.length})`}
        </Typography>
        {isFetching ? (
          <CircularProgress />
        ) : (
          details.map((detail) => (
            <ReturnItemRow
              key={detail.id}
              detail={detail}
              canEdit={hasUpdatePermission && !!detail.product}
              onChanged={handleConditionChanged}
            />
          ))
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => onClose(isChanged)}
          color="danger"
          disabled={isFetching}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ReturnItemRow({ detail, canEdit, onChanged }) {
  const classes = useStyles();
  const layoutDispatch = useLayoutDispatch();
  const [isEditing, setIsEditing] = useState(false);
  const [condition, setCondition] = useState(detail.condition || "");
  const [isSaving, setIsSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Signed S3 links are served as downloads, so photos are viewed in-app instead.
  const [previewUrl, setPreviewUrl] = useState(null);

  const sku = detail.product?.sku;
  const fromLocation = CONDITION_LOCATIONS[detail.condition];
  const toLocation = CONDITION_LOCATIONS[condition];
  // Only a shelved item with a product has stock that follows the new condition.
  const movesStock =
    detail.is_published && !!sku && !!toLocation && fromLocation !== toLocation;

  const cancelEdit = () => {
    setCondition(detail.condition || "");
    setIsEditing(false);
  };

  const saveCondition = () => {
    setConfirmOpen(false);
    setIsSaving(true);
    changeReturnCondition({
      body: { detail_id: detail.id, condition },
      responseSetter: (res) => {
        setIsSaving(false);
        const { success, message } = res || {};
        popupNotification({
          dispatch: layoutDispatch,
          message: message || "Server Error",
          status: success ? "success" : "error",
        });
        if (success) {
          setIsEditing(false);
          onChanged(detail.id, condition);
        }
      },
    });
  };

  const handleSave = () => {
    if (condition === detail.condition) {
      return setIsEditing(false);
    }
    if (movesStock) {
      return setConfirmOpen(true);
    }
    saveCondition();
  };

  return (
    <div className={classes.itemRow}>
      <Typography className={classes.itemSku}>{sku || "Not Ours"}</Typography>
      <div className={classes.itemCondition}>
        {isEditing ? (
          <>
            <TextField
              select
              size="small"
              variant="outlined"
              value={condition}
              onChange={({ target: { value } }) => setCondition(value)}
              disabled={isSaving}
              className={classes.conditionSelect}
            >
              {DETAIL_CONDITIONS.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </TextField>
            <MUIButton
              size="small"
              color="primary"
              variant="contained"
              onClick={handleSave}
              disabled={isSaving || !condition}
            >
              Save
            </MUIButton>
            <MUIButton size="small" onClick={cancelEdit} disabled={isSaving}>
              Cancel
            </MUIButton>
            {isSaving ? <CircularProgress size={18} /> : null}
          </>
        ) : (
          <>
            <StatusLabel status={detail.condition || "Not Ours"} />
            {canEdit ? (
              <IconButton
                size="small"
                aria-label="change-condition"
                title="Change condition"
                onClick={() => setIsEditing(true)}
              >
                <Edit size={0.8} />
              </IconButton>
            ) : null}
          </>
        )}
      </div>
      {detail.attachments?.length ? (
        <div className={classes.itemThumbnails}>
          {detail.attachments.map((item) => (
            <Tooltip
              key={item.id}
              placement="left"
              classes={{ tooltip: classes.imagePreview }}
              title={
                <img
                  src={item.signed_url}
                  alt={sku || "Return item"}
                  className={classes.imagePreviewImage}
                />
              }
            >
              <img
                src={item.signed_url}
                alt={sku || "Return item"}
                className={classes.itemThumbnail}
                onClick={() => setPreviewUrl(item.signed_url)}
              />
            </Tooltip>
          ))}
          <Dialog
            open={!!previewUrl}
            onClose={() => setPreviewUrl(null)}
            maxWidth="lg"
          >
            <img
              src={previewUrl || ""}
              alt={sku || "Return item"}
              className={classes.imageViewer}
              onClick={() => setPreviewUrl(null)}
            />
          </Dialog>
        </div>
      ) : null}
      <ConfirmDialog
        open={confirmOpen}
        title="Change Condition"
        confirmText="Move & Save"
        children={
          <Typography>
            {`${sku} will be marked ${condition} and moved from ${fromLocation} to ${toLocation}. Continue?`}
          </Typography>
        }
        onConfirm={saveCondition}
        onClose={() => setConfirmOpen(false)}
      />
    </div>
  );
}
